// Dashboard page for KATA Dashboard

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../stores/authStore';
import { useDashboardStore } from '../stores/dashboardStore';
import { useWorkshopStore, useWorkshops } from '../stores/workshopStore';
import { useDashboardMode } from '../contexts/DashboardModeContext';
import StatsCards from '../components/dashboard/StatsCards';
import DepartmentDistribution from '../components/dashboard/DepartmentDistribution';
import CompactDepartmentOverview from '../components/dashboard/CompactDepartmentOverview';
import DateRangeFilter from '../components/common/DateRangeFilter';
import { 
  Monitor, 
  Maximize2, 
  Minimize2, 
  Eye, 
  Users, 
  MapPin, 
  TrendingUp,
  Calendar,
  CheckCircle,
  Clock,
  AlertTriangle
} from 'lucide-react';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { refreshAll, deadlineFrom, deadlineTo, setDeadlineFilter } = useDashboardStore();
  const { fetchWorkshops } = useWorkshopStore();
  const { workshops, totalCount, isLoading: workshopsLoading } = useWorkshops();
  const { isAllInOneMode, toggleAllInOneMode } = useDashboardMode();

  // Load dashboard data on component mount
  useEffect(() => {
    refreshAll();
    fetchWorkshops();
  }, [refreshAll, fetchWorkshops]);

  // Calculate workshop statistics
  const workshopStats = {
    total: totalCount || 0,
    ongoing: workshops?.filter(w => w.status === 'in_progress').length || 0,
    completed: workshops?.filter(w => w.status === 'fully_implemented').length || 0,
    planned: workshops?.filter(w => w.status === 'new').length || 0,
    // Calculate late workshops (in progress workshops past deadline)
    late: workshops?.filter(w => {
      if (w.status !== 'in_progress' || !w.deadline) return false;
      const deadline = new Date(w.deadline);
      const now = new Date();
      return now > deadline;
    }).length || 0,
  };


  return (
    <div className={`${isAllInOneMode ? 'p-4' : 'p-6'}`}>
      {/* Header with All-in-One Toggle */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className={`${isAllInOneMode ? 'text-2xl' : 'text-3xl'} font-bold text-gray-900`}>
            Welcome back, {user?.full_name?.split(' ')[0] || user?.username}!
          </h1>
          <p className="mt-2 text-gray-600">
            Here's what's happening with your KATA initiatives today.
          </p>
        </div>
        
        {/* All-in-One Mode Toggle */}
        <div className="flex items-center gap-4">
          <DateRangeFilter
            startDate={deadlineFrom}
            endDate={deadlineTo}
            onStartDateChange={(date) => setDeadlineFilter(date, deadlineTo)}
            onEndDateChange={(date) => setDeadlineFilter(deadlineFrom, date)}
            onClear={() => setDeadlineFilter(undefined, undefined)}
          />
          <button
            onClick={toggleAllInOneMode}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
              isAllInOneMode
                ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {isAllInOneMode ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            {isAllInOneMode ? 'Standard View' : 'All in One Screen'}
          </button>
        </div>
      </div>

      {isAllInOneMode ? (
        /* All-in-One Screen Layout - TRUE single screen, no scrolling */
        <div className="h-[calc(100vh-6rem)] overflow-hidden flex flex-col gap-3">
          {/* Stats Cards - Compact grid */}
          <div className="flex-shrink-0">
            <StatsCards compact />
          </div>

          {/* Department Overview - Compact grid */}
          <div className="bg-white rounded-lg shadow p-3 flex-1 min-h-0">
            <h2 className="text-base font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <Users size={18} className="text-purple-600" />
              Department Overview
            </h2>
            <div className="h-full overflow-auto">
              <CompactDepartmentOverview />
            </div>
          </div>
        </div>
      ) : (
        /* Standard Layout */
        <div className="space-y-8">
          {/* Stats Cards */}
          <div>
            <StatsCards />
          </div>

          {/* Department Distribution */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Department Distribution
            </h2>
            <DepartmentDistribution />
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;